import http from '@/api/http'
import { clientPaymentsPageLimit } from '@/config/api'
import { majorToMinor } from '@/utils/majorToMinor'
import { cursorParams, type PageMeta } from '@/api/pagination'

export interface ClientPayment {
  // One payment from GET `/payments/clients/{client_id}` (cursor-paginated, DESCENDING by date).

  id: number
  // Payment id.

  amount: number
  // Amount in major currency units (see backend contract); see `clientPaymentAmountMinorUnits` for `MoneyCell`.

  date: string
  // ISO 8601 datetime of the payment.

  subscription_id?: string | null
  // Subscription bought with this payment; null/absent when none.

  subscription_type_id?: string | null
  // Catalog plan of that subscription (see `SubscriptionType`); null/absent when none.
}

/**
 * Major-to-minor units conversion for `MoneyCell`. Returns `undefined` when
 * the amount is missing or non-finite so the cell can show its placeholder.
 */
export function clientPaymentAmountMinorUnits(
  payment: Pick<ClientPayment, 'amount'>,
): number | undefined {
  return majorToMinor(payment.amount)
}

export interface ClientPaymentsResponse extends PageMeta {
  // Paginated list payload from GET `/payments/clients/{client_id}`.

  items: ClientPayment[]
  // Page of payments for one client.
}

/**
 * Fetches a page of payments for a single client. Same cursor/limit contract
 * as `fetchClients` and the other paginated list fetchers.
 *
 * @param clientId - Person id of the client.
 * @param cursor - Optional; pass `next_cursor` from a previous page for the next page.
 */
export async function fetchClientPayments(
  clientId: string,
  cursor?: number,
): Promise<ClientPaymentsResponse> {
  const { data } = await http.get<ClientPaymentsResponse>(`/payments/clients/${clientId}`, {
    params: cursorParams(clientPaymentsPageLimit, cursor),
  })
  if (import.meta.env.DEV) {
    console.log(`[fetchClientPayments] GET /payments/clients/${clientId} response`, data)
  }
  return data
}
